<template>
  <div id="customize-component">
    <div id="color-inputs">
      <div class="color-input-container">
        <p>Fill</p>
        <div id="fill-circle" ref="fillElement" @click="handleToggleColorPickr"></div>
      </div>
      <div class="color-input-container">
        <p>Border</p>
        <div id="border-circle" ref="borderElement" @click="handleToggleColorPickr"></div>
      </div>
    </div>
    <div id="range-inputs">
      <div class="range-wrap">
        <p>Border Width</p>
        <input
          type="range"
          min="0"
          max="100"
          class="slider"
          ref="borderWidthRange"
          @input="handleBorderWidth"
        />
        <div>{{ borderWidth }}px</div>
      </div>
      <div class="range-wrap">
        <p>Transparency</p>
        <input
          type="range"
          min="0"
          max="100"
          class="slider"
          ref="transparencyRange"
          @input="handleTransparency"
        />
        <div>{{ opacity }}%</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { fabric } from "fabric";

import { store } from "@/store";

import { updateClipArt } from "@/canvas-api/UserObjects/ClipArt";
import { updateShape } from "@/canvas-api/UserObjects/SvgShape";

import usePickr from "@/composables/usePickr";
import useSlider from "@/composables/useSlider";

import { ColorPickrState } from "@/types";
import { opacity, borderWidth, fillElement, borderElement, borderWidthRange, transparencyRange } from "@/canvas-api/EventListeners";

export default defineComponent({
  name: "Customize",
  setup() {
    const currentActiveObject = computed<fabric.Object>(() => store.getters.currentActiveObject);
    const colorPickrState = computed<ColorPickrState>(() => store.getters.colorPickrState);

    const handleBorderWidth = (e: { target: HTMLInputElement }): void => {
      const value = useSlider(e.target.value, borderWidthRange);

      if (value !== undefined && currentActiveObject.value) {
        borderWidth.value = value / 10;

        switch (currentActiveObject.value.name) {
          case "clipArt": {
            updateClipArt("strokeWidth", borderWidth.value);
            break;
          }
          case "svgShape": {
            updateShape("strokeWidth", borderWidth.value);
            break;
          }
        }
      }
    };

    const handleTransparency = (e: { target: HTMLInputElement }): void => {
      const value = useSlider(e.target.value, transparencyRange);

      if (value !== undefined && currentActiveObject.value) {
        opacity.value = value;

        switch (currentActiveObject.value.name) {
          case "clipArt": {
            updateClipArt("opacity", 1 - opacity.value / 100);
            break;
          }
          case "svgShape": {
            updateShape("opacity", 1 - opacity.value / 100);
            break;
          }
        }
      }
    };

    const handleToggleColorPickr = (event: { target: HTMLDivElement }): void => {
      const TARGET = event.target;

      switch (TARGET.getAttribute("id")) {
        case "fill-circle": {
          let payload = {
            ...colorPickrState.value,
            isOpen: false,
            id: TARGET.getAttribute("id")!,
            property: {
              name: "fill",
              value: TARGET.style.backgroundColor,
            },
          };

          usePickr(payload);
          break;
        }
        case "border-circle": {
          let payload = {
            ...colorPickrState.value,
            isOpen: false,
            id: TARGET.getAttribute("id")!,
            property: {
              name: "stroke",
              value: TARGET.style.backgroundColor,
            },
          };

          usePickr(payload);
          break;
        }
        default: {
          throw new Error("Missing target id!, Customize.vue");
        }
      }
    };

    onMounted(() => {
      const isGroup = (obj: fabric.Object): obj is fabric.Group => {
        const tmp = obj as fabric.Group;

        if (tmp._objects) {
          return true;
        } else {
          return false;
        }
      };

      if (isGroup(currentActiveObject.value)) {
        const activeObjStrokeWidth = (currentActiveObject.value._objects[0].strokeWidth as number) * 10;
        const activeObjOpacity = 100 - (currentActiveObject.value.opacity as number) * 100;

        if (borderWidthRange.value && transparencyRange.value) {
          borderWidthRange.value.value = activeObjStrokeWidth.toString();
          transparencyRange.value.value = activeObjOpacity.toString();
        }

        borderWidth.value = activeObjStrokeWidth;
        opacity.value = activeObjOpacity;

        if (fillElement.value && currentActiveObject.value.fill) {
          fillElement.value.style.background = currentActiveObject.value._objects[0].fill as string;
        }

        if (borderElement.value && currentActiveObject.value.stroke) {
          borderElement.value.style.background = currentActiveObject.value._objects[0]
            .stroke as string;
        }
      } else {
        const activeObjStrokeWidth = (currentActiveObject.value.strokeWidth as number) * 10;
        const activeObjOpacity = 100 - (currentActiveObject.value.opacity as number) * 100;

        if (borderWidthRange.value && transparencyRange.value) {
          borderWidthRange.value.value = activeObjStrokeWidth.toString();
          transparencyRange.value.value = activeObjOpacity.toString();
        }

        borderWidth.value = activeObjStrokeWidth;
        opacity.value = activeObjOpacity;

        if (fillElement.value && currentActiveObject.value.fill) {
          fillElement.value.style.background = currentActiveObject.value.fill as string;
        }

        if (borderElement.value && currentActiveObject.value.stroke) {
          borderElement.value.style.background = currentActiveObject.value.stroke;
        }
      }
    });

    return {
      transparencyRange,
      borderWidthRange,
      fillElement,
      borderElement,
      opacity,
      borderWidth,
      handleBorderWidth,
      handleTransparency,
      handleToggleColorPickr,
    };
  },
});
</script>

<style scoped>
#customize-component {
  width: 90%;
  height: calc(100% - 75px);
  margin: 20px auto 0 auto;
}

#color-inputs,
#range-inputs {
  width: 100%;
}

#color-inputs {
  display: flex;
  justify-content: space-evenly;
  margin-bottom: 20px;
}

.color-input-container {
  width: 75px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* align-items: center; */
}

#color-inputs p {
  font-weight: 400;
  font-size: 16px;
  margin-left: 12.5px;
}

#fill-circle,
#border-circle {
  width: 50px;
  height: 50px;
  background: #e6e6e6;
  border: 3px solid white;
  border-radius: 50%;
  margin: auto;
}

#border-circle {
  background: #0001ba;
}

.range-wrap {
  width: 100%;
  height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
}

.range-wrap p {
  color: #dcdcdc;
  font-weight: 400;
  font-size: 16px;
  padding-bottom: 2px;
}

.range-wrap input {
  width: 100%;
  background: 0 0;
}

.range-wrap div {
  margin: 0 auto;
}
</style>
